import { EventImpl } from '@fullcalendar/core/internal'
import viLocale from '@fullcalendar/core/locales/vi'
import dayGridPlugin from '@fullcalendar/daygrid'
import { default as FullCalendar } from '@fullcalendar/react'
import { Button } from '@mui/material'
import { useAuth } from 'components/auth/AuthProvider'
import StaffSidebar from 'components/staff/timesheet/staffSideBar/StaffSidebar'
import { parseISO } from 'date-fns'
import 'fullcalendar.css'
import moment from 'moment-timezone'
import React, { useEffect } from 'react'
import { useStatusStore } from 'stores/status.store'
import DialogForm from '../../components/staff/submitForm/DialogForm'
import { TimeSheetItem } from '../../components/staff/timesheet/TimeSheetItem'
import { useLoadingStore } from '../../stores/loading.store'
import { useTimeSheetStore } from '../../stores/timeSheet.store'
import { TimeRecordType } from '../../types/timeSheet'
export interface EventInput {
  title: string
  start: Date
  extendedProps: TimeRecordType
  allDay?: boolean
  staffCode: string
}

export interface MonthlySummary {
  month: number
  year: number
  totalWorks: number
  totalLate: number
  totalWfhNum: number
  totalOtTime: number
  totalRemainingDay: number
}

const customDayNames = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7']

const TimeSheet = () => {
  const setIsGlobalLoading = useLoadingStore((state) => state.setIsGlobalLoading)
  const isGlobalLoading = useLoadingStore((state) => state.isGlobalLoading)

  // const [timeRecords, setTimeRecords] = React.useState<EventInput[] | []>([])
  const [eventClicked, setEventClicked] = React.useState<EventImpl | null>(null)
  const [thisMonthSummary, setThisMonthSummary] = React.useState<MonthlySummary | null>(null)
  const [currentMonth, setCurrentMonth] = React.useState<number>(new Date().getMonth() + 1)
  const [currentYear, setCurrentYear] = React.useState<number>(new Date().getFullYear())
  const setIsOpenSubmitForm = useStatusStore((state) => state.setIsOpenSubmitForm)

  const [monthlySummary, timeRecords, setTimeRecords, staffSelectedCode, setStaffSelectedCode] =
    useTimeSheetStore((state) => [
      state.monthlySummary,
      state.timeRecords,
      state.setTimeRecords,
      state.staffSelectedCode,
      state.setStaffSelectedCode
    ])
  const auth = useAuth()

  const isMobile = () => window.innerWidth < 768
  const currentDate = new Date()

  const fetchDataTimeSheet = (month: number, year: number) => {
    setIsGlobalLoading(true)
    if (!auth?.user) return
    const isExist = timeRecords.some((event) => {
      const startDate = new Date(event.start)
      return (
        startDate.getMonth() + 1 === month &&
        startDate.getFullYear() === year &&
        event.staffCode === staffSelectedCode
      )
    })

    if (isExist) {
      setIsGlobalLoading(false)
      return
    }
    setTimeRecords(month, year, staffSelectedCode)
      .catch(() => {
        // alert("Can't get time records")
      })
      .finally(() => {
        setIsGlobalLoading(false)
      })
  }

  useEffect(() => {
    setStaffSelectedCode(auth?.user?.staffCode ?? '')
    const currentMonth = new Date().getMonth() + 1
    const currentYear = new Date().getFullYear()
    fetchDataTimeSheet(currentMonth, currentYear)
  }, [])

  useEffect(() => {
    fetchDataTimeSheet(currentMonth, currentYear)
  }, [staffSelectedCode])

  useEffect(() => {
    setThisMonthSummary(
      monthlySummary.find(
        (summary) => summary.month === currentMonth && summary.year === currentYear
      ) || null
    )
  }, [monthlySummary])

  // useEffect(() => {
  //   fetchDataTimeSheet(currentMonth, currentYear)
  // }, [currentMonth, currentYear])

  const handleSubmitFormClick = (event: EventImpl) => {
    if (staffSelectedCode != auth?.user?.staffCode) return
    setEventClicked(event)
    setIsOpenSubmitForm(true)
  }

  const handleDatesSet = (info: any) => {
    const month = info.view.currentStart.getMonth() + 1 // Months are 0-indexed, so add 1
    const year = info.view.currentStart.getFullYear()
    setCurrentMonth(month)
    setCurrentYear(year)
    fetchDataTimeSheet(month, year)
    setThisMonthSummary(
      monthlySummary.find((summary) => summary.month === month && summary.year === year) || null
    )
  }

  if (!auth?.user) return <div>Not found</div>

  return (
    <div className="flex flex-row h-full">
      <StaffSidebar />
      <FullCalendar
        plugins={[dayGridPlugin]}
        height={isMobile() ? 'auto' : '50vh'}
        initialView="dayGridMonth"
        locale={viLocale}
        events={timeRecords}
        customButtons={{
          totalWorksButton: {
            text: `Làm việc: ${thisMonthSummary?.totalWorks ? thisMonthSummary?.totalWorks.toFixed(2) : '--'} (h)`
          },
          totalWfhNumButton: {
            text: `WFH: ${thisMonthSummary?.totalWfhNum ? thisMonthSummary?.totalWfhNum : '--'} (lần)`
          },
          maxWfhNumButton: {
            text: `WFH: 5 (lần)`
          },
          totalOtTime: {
            text: `OT: ${thisMonthSummary?.totalOtTime ? thisMonthSummary?.totalOtTime?.toFixed(2) : '--'} (h)`
          },
          totalLateButton: {
            text: `Đi trễ về sớm: ${thisMonthSummary?.totalLate ? thisMonthSummary?.totalLate?.toFixed(2) : '--'} (h)`
          }
        }}
        headerToolbar={{
          left: 'title', // Tiêu đề ở bên trái
          center: `totalWorksButton ${thisMonthSummary?.totalWfhNum == 5 ? 'maxWfhNumButton' : 'totalWfhNumButton'} totalOtTime totalLateButton`,
          right: 'today prev,next' // Các nút today, next, prev ở bên phải
        }}
        titleFormat={() => {
          return 'Tháng ' + currentMonth + '/' + currentYear
        }}
        eventClassNames="bg-transparent"
        dayCellClassNames={(arg) => {
          for (let i = 0; i < timeRecords.length; i++) {
            if (
              moment(arg.date).tz('Asia/Ho_Chi_Minh').format('YYYY-MM-DD') ===
              moment(timeRecords[i]?.extendedProps?.date)
                .tz('Asia/Ho_Chi_Minh')
                .format('YYYY-MM-DD')
            ) {
              if (
                timeRecords[i].extendedProps.type === 'Off_P' ||
                timeRecords[i].extendedProps.type === 'Off_SP' ||
                timeRecords[i].extendedProps.type === 'Off_U' ||
                timeRecords[i].extendedProps.type === 'Off_S' ||
                timeRecords[i].extendedProps.type === 'Off_M' ||
                timeRecords[i].extendedProps.type === 'Off_P_0.5' ||
                timeRecords[i].extendedProps.type === 'Off_U_0.5' ||
                timeRecords[i].extendedProps.type === 'Off_S_0.5' ||
                timeRecords[i].extendedProps.type === 'Off_M_0.5'
              ) {
                return 'relative bg-gray-100'
              } else if (timeRecords[i].extendedProps.type === 'WFH') {
                return 'relative bg-orange-50'
              } else {
                return 'relative'
              }
            }
          }
          return 'relative'
        }}
        weekends={false}
        dayHeaderContent={(arg) => customDayNames[arg.date.getUTCDay()]}
        datesSet={handleDatesSet}
        contentHeight="auto" // Allow the content to expand to accommodate the custom content
        eventContent={function (arg) {
          const { type, firstIn, lastOut } = arg.event.extendedProps as TimeRecordType
          const { start } = arg.event
          const eventDate = parseISO(arg.event.startStr)
          const isFutureDate = eventDate > currentDate

          if (
            type === '' &&
            !firstIn &&
            !lastOut &&
            !(start && [6, 0].includes(start.getDay())) &&
            !isFutureDate
          ) {
            return (
              <div className="!border-none flex items-stretch justify-center mt-5">
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  sx={{
                    '&:hover': {
                      backgroundColor: `${staffSelectedCode == auth?.user?.staffCode ? '#b72136' : '#FF4842'} `,
                      cursor: `${staffSelectedCode == auth?.user?.staffCode ? 'pointer' : 'not-allowed'} `
                    }
                  }}
                  onClick={() => {
                    handleSubmitFormClick(arg.event)
                  }}
                  disabled={isGlobalLoading}
                >
                  <span className="flex gap-2">
                    -<div className="text-white font-bold"></div>-
                  </span>
                </Button>
              </div>
            )
          } else {
            return (
              <TimeSheetItem
                handleSubmitFormClick={handleSubmitFormClick}
                event={arg.event}
                arg={arg.event.extendedProps as TimeRecordType}
              />
            )
          }
        }}
      />
      <DialogForm eventClicked={eventClicked}></DialogForm>
    </div>
  )
}

export default TimeSheet
